import React, { useState } from 'react'
import { get, keyBy, uniq } from 'lodash'
import className from 'classnames'
import moment from 'moment'

import {useCheckIds} from '../hooks'

import OverflowH from './OverflowH'
import DotTooltip from './DotTooltip'
import TableHeader from './TableHeader'
import {CSRF_TOKEN, requestCallback, addNotification} from '../lib/helpers'
import axios from 'axios'
import {canEdit, canKenta} from '../lib/policies'
import Notification from './Notification'
import BulkPenaltyTools from './BulkPenaltyTools'
import BulkEditInformationModal from './BulkEditInformationModal'

import Modal from './Modal'
import { useModal } from '../hooks'

const LettersList = ({
  letters = [],
  total = 0,
}) => {
  const {
    checkIds,
    renderCheckBox,
    renderCheckBoxForCheckAll,
    shouldDisableAction,
  } = useCheckIds({
    data: letters,
    openUrl: 'letters',
  })

  const [editModalActive, setEditModal] = useState(false)

  const [
    reportModal,
    reportModalSet,
  ] = useModal({
    keyboardBindings: true,
  })

  const bulkDelete = () => {
    if (checkIds.length > 0) {
      requestCallback(axios.delete(`/${gon.currentProvider}/api/bulk/letters`, {
        data: {
          ids: checkIds,
          authenticity_token: CSRF_TOKEN
        }
      }))
    }
  }

  const openModal = () => {
    if (checkIds.length > 0) {
      setEditModal(true)
    }
  }

  const openReportModal = () => {
    if (checkIds.length > 0) {
      reportModalSet(true)
    }
  }

  const bulkReport = (reportedCount) => {
    requestCallback(axios.post(`/${gon.currentProvider}/api/bulk/letters/report`, {
      ids: checkIds,
      reported_count: reportedCount,
      authenticity_token: CSRF_TOKEN
    }))
  }

  const retrieveSentUserIdsFromLetterIds = (letterIds = []) => {
    const lettersById = keyBy(letters, 'id')
    return uniq(letterIds.map(id => get(lettersById, `${id}.fromUser.id`)).filter(id => typeof id !== 'undefined'))
  }

  const reportLetter = (letterId) => {
    if (confirm('Are you sure to report this letter?')) {
      axios.post(`/${gon.currentProvider}/api/letters/${letterId}/report`, { authenticity_token: CSRF_TOKEN,})
        .then((res) => {
          if (res.status === 200) {
            addNotification({
              message: `Letter ${letterId} reported successfully`,
              type: Notification.NOTIFICATION_TYPES.success,
            })
          }
        })
        .catch((error) => {
          addNotification({
            message: 'Something went wrong',
            type: Notification.NOTIFICATION_TYPES.error,
          })
        })
        .then(() => {
          window.location.reload()
        })
    }
  }

  return (
    <React.Fragment>
      <BulkEditInformationModal
        title="Bulk Edit Letter"
        isActive={editModalActive}
        templateApiUrl="/api/letter_templates"
        bulkUpdateApiUrl={`/${gon.currentProvider}/api/bulk/letters`}
        updatedIds={checkIds}
        updatedAtField="text"
        onClose={() => setEditModal(false)}
      />
      <div className='MainHeader'>
        <div className='MainHeader__left'>
          <h2 className='MainHeader__h2'>Letters list</h2>
          <p className='MainHeader__left__p'>Total {total.toLocaleString()} letters</p>
        </div>
      </div>

      <div className='TableChecker'>
        {renderCheckBoxForCheckAll()}

        <div className='TableChecker__right'>
          {canEdit('Post') && (
            <a
              onClick={openReportModal}
              className="TableChecker__right__a TableChecker__right__a--flag"
              title="Report"
              disabled={shouldDisableAction}
            />
          )}
          {canEdit('User') && (
            <a
              onClick={openModal}
              className="TableChecker__right__a TableChecker__right__a--pencil"
              title="Edit"
              disabled={shouldDisableAction}
            />
          )}
          {canEdit('User') && (
            <a
              className='TableChecker__right__a TableChecker__right__a--trash'
              onClick={bulkDelete}
              disabled={shouldDisableAction}
            />
          )}
          {canKenta('User') && (
            <BulkPenaltyTools
              userIds={retrieveSentUserIdsFromLetterIds(checkIds)}
            />
          )}
        </div>
      </div>

      <div className='Table Table--letters-list'>
        {(() => {
          const columnHeaders = [
            {
              label: '',
            },
            {
              label: 'ID',
              sortKey: 'review_id',
            },
            {
              label: 'Form user',
              sortKey: 'reviewer_id'
            },
            {
              label: 'Letter message',
            },
            {
              label: 'To user',
              sortKey: 'user_id',
            },
            {
              label: 'Score',
              sortKey: 'score',
            },
            {
              label: 'Reported'
            },
            {
              label: 'Created date',
              sortKey: 'created_at',
            },
            {
              label: 'Manage',
            },
          ]

          return (
            <div className='Table__row'>
              {columnHeaders.map((header, key) => (
                <TableHeader
                  key={key}
                  header={header}
                />
              ))}
            </div>
          )
        })()}

        {letters.map((letter) => (
          <div
            className={className('Table__row', {
              'Table__row--active': checkIds.includes(letter.id),
              'Table__row--hiding': get(letter, 'isHidden', 0)
            })}
            key={letter.id}
            data-x={letter}
          >
            <div className='Table__col'>
              {renderCheckBox(letter)}
            </div>

            <div className='Table__col'>{get(letter, 'id')}</div>

            <div className='Table__col'>
              <DotTooltip
                user={letter.fromUser}
              />
            </div>

            <div className='Table__col'>
              <OverflowH
                text={letter.comment}
              />
            </div>

            <div className='Table__col'>
              <DotTooltip
                user={letter.toUser}
              />
            </div>

            <div className='Table__col'>{letter.score}</div>
            <div className='Table__col'>{letter.reportedCount}</div>

            <div
              className='Table__col'
              dangerouslySetInnerHTML={
                {
                  __html: letter.createdAt !== null ?
                    moment(letter.createdAt).format('YYYY-MM-DD<br />HH:mm')
                  :
                    'N/A'
                }
              }
            />

            <div className='Table__col'>
              <div className='Table__controls'>
                <a
                  className='Table__controls__a Table__controls__a--file-history'
                  href={`/${gon.currentProvider}/report_histories/letters/${get(letter, 'id')}`}
                />

                {canEdit('Post') && (
                  <a
                    onClick={() => reportLetter(get(letter, 'id'))}
                    className="TableChecker__right__a TableChecker__right__a--flag"
                    title="Report"
                  />
                )}

              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal
        isActive={reportModal}
        onToggleModal={() => {
          reportModalSet(false)
        }}
      >
        <div className='BannedWordModal'>
          <h3 className='Modal__h3'>Update report count</h3>

          <form
            className='BannedWordModal__form'
            onSubmit={(e) => {
              e.preventDefault()
              const $els = e.target.elements
              const reportCount = get($els, 'reported_count.value').trim()

              requestCallback(bulkReport(reportCount))
            }}
          >
            <input
              type='number'
              min='0'
              defaultValue='3'
              className='Input'
              name='reported_count'
              placeholder='Reported Count'
              required
            />

            <div className='BannedWordModal__form__buttons'>
              <a
                className='Button Button--gray-light'
                onClick={() => {
                  reportModalSet(false)
                }}
              >
                Cancel
              </a>

              <button className='Button Button--blue'>
                Update
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </React.Fragment>
  )
}

export default LettersList
